import React, {useEffect, useRef, useState} from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { useNavigate } from "react-router-dom";
import "./Dashboard.css";
import {auth, database, storage, logout, resultsStorage, defaultAssetsStorage} from "../../tools/firebase";
import { ref, onValue } from "firebase/database";
import { ref as sRef } from "firebase/storage";
import axios from "axios";

import {track} from "@amplitude/analytics-browser";

import { performance } from 'universal-perf-hooks';

import {
    Affix,
    Breadcrumb,
    Button,
    Layout,
    Menu,
    Typography,
    message,
    Spin,
    Divider,
    Popconfirm,
    Row,
    Col,
    Form,
    Input,
    Radio,
    Checkbox,
    Space,
    Select,
    Modal,
    InputNumber,
} from 'antd';
import SourceParamsPanel from "../ParamsPanel/SourceParamsPanel";
import AssetsMenu from "../AssetsMenu";
import DashboardHeader from "../DashboardHeader";
import NoSourcePanel from "../SourceAndResultPanels/NoSourcePanel";
import SourcePanel from "../SourceAndResultPanels/SourcePanel";
import {getDownloadURL} from "firebase/storage";
import FeedbackPanel from "../FeedbackPanel";
import ResultParamsPanel from "../ParamsPanel/ResultParamsPanel";
import ResultStagePanel from "../SourceAndResultPanels/ResultStagePanel";
import {GLOBAL_CONFIG} from "../../tools/global_config";
import {useTheme} from "../ThemeSwitching/use-theme";
import AssetsMenuList from "../AssetsMenuList";
import {useTips} from "../Settings/use-tips";

const { Title, Text } = Typography;

function Dashboard() {
    const [user, loading, error] = useAuthState(auth);
    const [darkMode, setDarkMode] = useTheme();
    const [assetsLoaded, setAssetsLoaded] = useState(false);
    const [assetUploading, setAssetUploading] = useState(false);
    const [name, setName] = useState("");
    const [token, setToken] = useState("");
    const [selectedSource, setSelectedSource] = useState({});
    const [selectedResult, setSelectedResult] = useState({});
    const [assetsList, setAssetsList] = useState([]);
    const [generating, setGenerating] = useState(false);
    const [resultStages, setResultStages] = useState({});
    const [boostedPrompt, setBoostedPrompt] = useState("");
    const [boosting, setBoosting] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showTips, setShowTips] = useTips();

    const navigate = useNavigate();

    const topRef = useRef(null);
    const bottomRef = useRef(null);

    const fetchToken = async () => {
        try {
            let newToken = await user?.getIdToken();
            setToken(newToken);
            return newToken;
        } catch (err) {
            console.error(err);
            alert(err);
        }
    }

    const fetchUserName = async () => {
        try {
            let guid = await user?.uid
            let email = await user?.email
            let dispName = await user?.displayName
            window.hj('identify', guid, {
                'email' : email
            });
            let resultName = !dispName ? email : dispName
            setName(resultName);
        } catch (err) {
            console.error(err);
            alert(err);
        }
    };

    const extractDownloadUrl = (asset, isDefault) => {
        let url = "";
        if (isDefault) {
            url = getDownloadURL(sRef(defaultAssetsStorage, asset.substring(asset.indexOf('/') + 1)));
        } else {
            url = getDownloadURL(sRef(storage, asset.substring(asset.indexOf('/') + 1)));
        }
        return url
    }

    const extractResultDownloadUrl = (result_path) => {
        let url = getDownloadURL(sRef(resultsStorage, result_path));
        return url
    }

    const updateAssetsList = async (snapshotVal) => {
        let result = [];
        for (let key in snapshotVal) {
            if (snapshotVal.hasOwnProperty(key)) {
                if (snapshotVal[key].hasOwnProperty('assetUrl')) {
                    let path = snapshotVal[key]["assetUrl"];
                    let name = path.substring(path.lastIndexOf('/') + 1);
                    if (name.includes("glb")) {
                        continue;
                    }
                    let downloadUrl = await extractDownloadUrl(path, snapshotVal[key].hasOwnProperty("automaticallyGenerated"));
                    let finalResult = {name: name, path: path, url: downloadUrl};
                    if (snapshotVal[key].hasOwnProperty("default_prompt")) {
                        finalResult["default_prompt"] = snapshotVal[key]["default_prompt"];
                    }
                    finalResult["rtdb_asset_key"] = key;
                    result.push(finalResult);
                }
            }
        }
        setAssetsLoaded(true);
        setAssetsList(result);
    }

    const getAssetsList = async () => {
        let assetsListRef = ref(database, '/users/assets/' + user?.uid);
        onValue(assetsListRef,snapshot => {
            updateAssetsList(snapshot.val());
        });
    }

    const uploadProps = (tkn) => {
        return {
            name: 'file',
            action: GLOBAL_CONFIG.upload_url,
            headers: {
                authorization: "Bearer " + tkn
            },
            showUploadList: false,
            method: 'POST',
            onChange(info) {
                if (info.file.status !== 'uploading') {
                    console.log(info.file, info.fileList);
                }
                if (info.file.status === 'done') {
                    setAssetUploading(false);
                    message.success(`${info.file.name} file uploaded successfully`);
                } else if (info.file.status === 'error') {
                    setAssetUploading(false);
                    message.error(`${info.file.name} file upload failed.`);
                }
            }
        }
    };

    const onResultClick = (result) => {
        track('result_clicked', {'path' : result.output_path});

        setSelectedResult(
            {
                ...result,
                name: result.output_path.substring(result.output_path.indexOf('/') + 1)
            }
        )
    }

    const onSourceClickCallback = () => {
        setSelectedResult({});
    }

    const onDeleteClicked = (selectedSourceUrl) => {
        let newResults = resultStages;
        delete newResults[selectedSourceUrl];
        setResultStages(newResults);
        setSelectedResult({});
    };

    const onSaveClickCallback = async (params) => {
        track('save_clicked', {'path': params.image_blob_path});
        let newToken = await fetchToken();

        setSaving(true);

        let config = {
            headers: {
                'Authorization': `Bearer ${newToken}`
            },
            params: params
        }

        try {
            const response = await axios.get(GLOBAL_CONFIG.save_as_asset_url, config);
            setSaving(false);
            message.success("Saved as asset!")
        } catch (e) {
            setSaving(false);
            message.error("Saving as asset failed: " + e.message);
        }
    }

    const onBoostPromptClicked = async (prompt) => {
        track('boost_prompt_clicked', {'init_text' : prompt});
        let newToken = await fetchToken();
        setBoosting(true);

        let config = {
            headers: {
                'Authorization': `Bearer ${newToken}`
            },
            params: {
                init_text: prompt
            }
        }

        try {
            const response = await axios.get(GLOBAL_CONFIG.prompt_boost_url, config);
            setBoosting(false);
            setBoostedPrompt(response.data[0].resulting_text);
        } catch (e) {
            setBoosting(false);
            message.error("Boosting prompt failed: " + e.message);
        }
    }

    const onCreateClickCallback = async (url, params)  => {
        track('generate_clicked', {...params, url: url});
        let newToken = await fetchToken();
        let start = performance.now();
        setGenerating(true);

        let config = {
            headers: {
                'Authorization': `Bearer ${newToken}`
            },
            params: params
        }

        if (selectedResult.hasOwnProperty("url")) {
            setSelectedResult({...selectedResult, params: params})
        } else {
            setSelectedSource({...selectedSource, params: params})
        }

        try {
            const response = await axios.get(url, config)
            setGenerating(false);

            if (response.status !== 200) {
                message.error("Failed to generate. Please try again.")
            } else {
                message.success("Results generated")
                let itemsWithUrls = [];
                for (let key in response.data) {
                    let item = response.data[key];
                    item.intermediate_timesteps = item.intermediate_timesteps.map(step => parseInt(step));
                    let url = await extractResultDownloadUrl(item.output_path);
                    itemsWithUrls.push({
                        ...item,
                        url: url,
                        prompt: params.prompt,
                        level: selectedResult.hasOwnProperty("url") ? selectedResult.level + 1 : 1
                    });
                }

                if (selectedResult.hasOwnProperty("url")) {
                    let sliced = resultStages[selectedSource.url].slice(0, selectedResult.level);
                    let newStages = resultStages;
                    newStages[selectedSource.url] = [...sliced, {items: itemsWithUrls}];
                    setResultStages(newStages)
                    setSelectedResult({...selectedResult, params: params});
                } else {
                    let newResult = resultStages;
                    newResult[selectedSource.url] = [{ items: itemsWithUrls}];
                    setResultStages(newResult);
                    setSelectedSource({...selectedSource, params: params});
                }

                let finish = performance.now();
                track('generation_time_ms', {...params, url: url, time: finish - start})
            }
        } catch (e) {
            setGenerating(false);
            message.error("Generating results failed: " + e.message);
        }
    };

    const sendDefaultAssetsCreationRequest = async () => {
        let newToken = await fetchToken();
        console.log(newToken);

        let config = {
            headers: {
                'Authorization': `Bearer ${newToken}`
            }
        }

        try {
            const response = await axios.get(GLOBAL_CONFIG.add_default_assets_url, config);

            if (response.status !== 200) {
                console.log("Failed to add default assets. Please try again.")
            } else {
                console.log("Default assets added");
            }
        } catch (e) {
            console.log("Error: " + e.message);
        }
    }

    useEffect(() => {
        if (loading) return;
        if (!user) return navigate("/closed-beta");
        track("dashboard_user_loaded");
        window.rdt('track', 'SignUp');
        window.pintrk('track', 'signup');
        fetchUserName();
        fetchToken();
        sendDefaultAssetsCreationRequest();
        getAssetsList();
    }, [user, loading]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({behavior: 'smooth'});
    }, [resultStages]);

    useEffect(() => {
        topRef.current?.scrollIntoView({behavior: 'smooth'});
    }, [selectedSource]);

    const menu = (
        <AssetsMenuList
            uploading={assetUploading}
            setUploading={setAssetUploading}
            loaded={assetsLoaded}
            assetsList={assetsList}
            onSelectCallback={(selection) => {
                track('source_selected', {...selection});
                setSelectedSource(selection);
                setSelectedResult({});
            }}
            uploadProps={uploadProps(token)}
            selectedIndex={selectedSource.key}
            onDeleteCallback={ async (rtdb_asset_id) => {
                track('source_delete', {"asset_id" : rtdb_asset_id})

                let newToken = await fetchToken();

                const config = {
                    method : "post",
                    url : GLOBAL_CONFIG.remove_asset_url,
                    headers: {
                        'Authorization': `Bearer ${newToken}`
                    },
                    params: {
                        "asset_id" : rtdb_asset_id
                    }
                }

                axios(config)
                    .then(res => {
                        message.success("Asset deleted")
                    })
                    .catch(err => {
                        message.error("Asset deletion failed: " + err.message);
                    })
            }}
        />
    );

    return (
        <Layout style={{fontFamily: "Avenir"}}>
            <div ref={topRef}/>
            <DashboardHeader name={name} token={token} showTips={showTips} setShowTips={setShowTips}/>
            <Layout style={{display: "flex"}}>

                {/* Menu panel */}
                <div>
                    <Affix >
                        <div>
                            { menu }
                        </div>
                    </Affix>
                </div>

                {/* Central panel */}
                <div style={{flex: "auto", minWidth: "0"}}>
                    <div
                        style={{
                            margin: 16,
                            backgroundColor: darkMode ? "#1f1f1f" : "white",
                            boxShadow: "rgba(0, 0, 0, 0.1) 0px 4px 12px"
                        }}
                    >
                        {
                            selectedSource.hasOwnProperty("key")
                                ? <SourcePanel
                                    selectedSource={selectedSource}
                                    onSourceClick={onSourceClickCallback}
                                    selectedResult={selectedResult}
                                    onDeleteClicked={onDeleteClicked}
                                />
                                : <NoSourcePanel/>
                        }
                    </div>

                    {
                        resultStages.hasOwnProperty(selectedSource.url) ?
                        resultStages[selectedSource.url].map((stage, index) =>
                            <div
                                style={{
                                    margin: 16,
                                    backgroundColor: darkMode ? "#1f1f1f" : "white",
                                    boxShadow: "rgba(0, 0, 0, 0.1) 0px 4px 12px"
                                }}
                            >
                                <ResultStagePanel
                                    key={`stage_${index}`}
                                    resultStage={stage}
                                    onImageClick={onResultClick}
                                    selectedResult={selectedResult}
                                />
                            </div>

                        )
                            : <div/>
                    }

                    {
                        resultStages.hasOwnProperty(selectedSource.url) ?
                        !resultStages[selectedSource.url].length ? <div/> :  <div ref={bottomRef} style={{marginTop: 192}}/>
                            : <div/>
                    }

                </div>

                {/* Params panel */}
                <div style={{width: 440}}>
                    <Affix offsetTop={16} style={{margin: 16}}>
                        <div>
                            <Row
                                style={{
                                    backgroundColor: darkMode ? "#1f1f1f" : "white",
                                    boxShadow: "rgba(0, 0, 0, 0.1) 0px 4px 12px"
                                }}
                            >
                                {
                                    selectedResult.hasOwnProperty("url")
                                        ? <ResultParamsPanel
                                            saving={saving}
                                            loading={generating}
                                            selectedResult={selectedResult}
                                            onSaveClickCallback={onSaveClickCallback}
                                            onCreateClickCallback={onCreateClickCallback}
                                        />
                                        : <SourceParamsPanel
                                            boosting={boosting}
                                            boostedPrompt={boostedPrompt}
                                            onBoostPromptClicked={onBoostPromptClicked}
                                            loading={generating}
                                            selectedSource={selectedSource}
                                            onCreateClickCallback={onCreateClickCallback}
                                            showTips={showTips}
                                        />
                                }
                            </Row>
                            {
                                selectedResult.hasOwnProperty("url")
                                    ? <div></div>
                                    : <Row
                                        style={{
                                            marginTop: 20,
                                            backgroundColor: darkMode ? "#1f1f1f" : "white",
                                            boxShadow: "rgba(0, 0, 0, 0.1) 0px 4px 12px"
                                        }}
                                    >
                                        <FeedbackPanel/>
                                    </Row>
                            }
                        </div>
                    </Affix>
                </div>
            </Layout>
        </Layout>
    );
}

export default Dashboard;